import { FC } from "react";

import { Stack } from "@mui/material";
import { FormType } from "@simplyk/common";

import { AssistantPaymentCard } from "../helpers/parsePaymentCards";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { TranslationKey, useTranslate } from "@/common/hooks/useTranslate";
import { FRONTEND_URL } from "@/common/routes/routes";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DonorPaymentControl } from "@/features/DonorDashboard/DonorPaymentDetailDrawer/types";
import { ExternalLink } from "@/icons/outlined";

const CONTROL_LABEL_KEYS = {
  cancel_renewal: "donorTransactionDetailDrawer.cancelRenewal",
  update_payment_method: "donorTransactionDetailDrawer.changePaymentMethod",
  edit_charge_date: "donorTransactionDetailDrawer.editChargeDate",
  change_amount: "donorTransactionDetailDrawer.changeAmount",
  adjust_tip: "donorTransactionDetailDrawer.adjustTip",
} as const satisfies Record<DonorPaymentControl, TranslationKey<"dashboard">>;

interface PaymentCardActionsProps {
  payment: AssistantPaymentCard;
  control: DonorPaymentControl | null;
  onOpenControl?: (payment: AssistantPaymentCard, control: DonorPaymentControl) => void;
}

export const PaymentCardActions: FC<PaymentCardActionsProps> = ({ payment, control, onOpenControl }) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const openControl = control && onOpenControl ? () => onOpenControl(payment, control) : null;
  if (!openControl && !payment.formPath) {
    return null;
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={1} data-test="donor-assistant-payment-card-actions">
      {control && openControl && (
        <DashboardButton
          variant="outlined"
          size="small"
          vibe={control === "cancel_renewal" ? "danger" : "neutral"}
          onClick={openControl}
        >
          {t("dashboard", CONTROL_LABEL_KEYS[control])}
        </DashboardButton>
      )}
      {payment.formPath && (
        <DashboardButton
          variant="text"
          size="small"
          vibe="brand"
          endIcon={<ExternalLink />}
          href={`${FRONTEND_URL}/${isoLocale}${payment.formPath}`}
          target="_blank"
        >
          {t(
            "dashboard",
            payment.formType === FormType.DonationForm
              ? "donorAssistant.paymentCard.donateAgain"
              : "donorAssistant.paymentCard.openForm"
          )}
        </DashboardButton>
      )}
    </Stack>
  );
};
